import React, { useState, ReactNode } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { Formik } from "formik";
import * as yup from "yup";

import Header from "components/Header";
import DocumentCategoryRow from "./document-category-row";
import { hasPermission, Permission } from "core/utils/roles";
import { useAuth } from "app/authHooks";
import { DocumentCategoryService } from "../api";
import { CreateDocumentCategoryInput, DocumentCategory } from "app/services/types.generated";

const TablePlaceholder: React.FC<{ children: ReactNode; colSpan: number }> = ({
  children,
  colSpan
}) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center">
      {children}
    </TableCell>
  </TableRow>
);

const documentCategorySchema = yup.object({
  id: yup.number()
    .required("ID is required")
    .positive("ID must be a positive number")
    .integer("ID must be an integer"),
  externalCategory: yup.string().required("Intelliflo Document Category is required"),
  externalSubCategory: yup.string().required("Intelliflo Document Sub-Category is required"),
  internalCategory: yup.string().required("Hubwise Document Category is required"),
  internalFileCategory: yup.string().required("Hubwise Document File Category is required"),
});

const DocumentCategoryTable: React.FC = () => {
  const { attributes } = useAuth();
  const theme = useTheme();
  const colors = theme.palette;
  const { enqueueSnackbar } = useSnackbar();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // API hooks
  const { data, isLoading, refetch } = DocumentCategoryService.useGetAllDocumentCategoriesQuery({
    limit: 1000
  });
  const [createDocumentCategory] = DocumentCategoryService.useCreateDocumentCategoryMutation();
  const [updateDocumentCategory] = DocumentCategoryService.useUpdateDocumentCategoryMutation();
  const [deleteDocumentCategory] = DocumentCategoryService.useDeleteDocumentCategoryMutation();

  const documentCategories = data?.getAllDocumentCategories?.data || [];

  const handleCreate = async (values: CreateDocumentCategoryInput) => {
    try {
      const response = await createDocumentCategory({ input: values });

      if (response.data?.createDocumentCategory?.success && response.data?.createDocumentCategory?.data) {
        enqueueSnackbar("Document category created successfully", { variant: "success" });
        setCreateDialogOpen(false);
        refetch();
      } else {
        enqueueSnackbar(
          response.data?.createDocumentCategory?.errorMessage || "Failed to create document category",
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar("Failed to create document category", { variant: "error" });
    }
  };

  const handleEdit = async (docId: string, updatedData: Partial<DocumentCategory>) => {
    const response = await updateDocumentCategory({
      input: {
        docId: docId,
        id: updatedData.id || 0,
        externalCategory: updatedData.externalCategory || "",
        externalSubCategory: updatedData.externalSubCategory || "",
        internalCategory: updatedData.internalCategory || "",
        internalFileCategory: updatedData.internalFileCategory || ""
      }
    });

    if (response.data?.updateDocumentCategory?.success && response.data?.updateDocumentCategory?.data) {
      refetch();
    } else {
      throw new Error(response.data?.updateDocumentCategory?.errorMessage || "Update failed");
    }
  };

  const handleDelete = async (docId: string) => {
    const response = await deleteDocumentCategory({ input: { docId } });

    if (response.data?.deleteDocumentCategory?.success && response.data?.deleteDocumentCategory?.data) {
      enqueueSnackbar("Document category deleted successfully", { variant: "success" });
      refetch();
    } else {
      const errorMessage = response.data?.deleteDocumentCategory?.errorMessage || "Failed to delete document category";
      enqueueSnackbar(errorMessage, { variant: "error" });
      throw new Error(errorMessage);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Header
          title="Document Category Mapping"
          subtitle="Manage document category mappings between Intelliflo and Hubwise"
        />
        {hasPermission("create:document-category" as Permission) && (
          <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
            <Button
              type="submit"
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add Document Category
            </Button>
          </Box>
        )}
      </Box>

      <Box
        flexGrow={1}
        m="30px 0 0 0"
        sx={{
          display: "flex",
          flexDirection: "column",
          "& .MuiDataGrid-root": {
            border: `1px solid ${colors.cardBorderColor}`,
            borderRadius: "2px",
          },
          "& .name-column--cell": {
            color: colors.grey[100],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-toolbarContainer": {
            backgroundColor: colors.cardBackground,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]}`,
          },
          "& .MuiDataGrid-paper": {
            backgroundColor: `${colors.blueAccent[700]} !important`,
          },
        }}
      >
        <TableContainer sx={{ flexGrow: 1, maxHeight: "65vh" }} component={Paper}>
          <Table stickyHeader aria-label="document category table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography noWrap variant="h5">
                    ID
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography noWrap variant="h5">
                    Intelliflo Document Category
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography noWrap variant="h5">
                    Intelliflo Document Sub-Category
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography noWrap variant="h5">
                    Hubwise Document Category
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography noWrap variant="h5">
                    Hubwise Document File Category
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography noWrap variant="h5">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TablePlaceholder colSpan={6}>
                  <CircularProgress color="primary" />
                </TablePlaceholder>
              )}

              {!isLoading && !documentCategories?.length && (
                <TablePlaceholder colSpan={6}>
                  <Typography variant="h5">No Document Categories Found</Typography>
                </TablePlaceholder>
              )}

              {!isLoading && documentCategories?.map((row: DocumentCategory) => (
                <DocumentCategoryRow
                  key={row.docId}
                  row={row}
                  attributes={attributes}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create Document Category Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4">Add Document Category Mapping</Typography>
        </DialogTitle>
        <Formik
          initialValues={{
            id: "" as any,
            externalCategory: "",
            externalSubCategory: "",
            internalCategory: "",
            internalFileCategory: "",
          }}
          validationSchema={documentCategorySchema}
          onSubmit={handleCreate}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            resetForm,
          }) => {
            const handleClose = () => {
              setCreateDialogOpen(false);
              resetForm();
            };

            return (
              <form onSubmit={handleSubmit}>
                <DialogContent>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="id"
                    label="ID"
                    type="number"
                    value={values.id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.id && Boolean(errors.id)}
                    helperText={(touched.id && errors.id) ? String(errors.id) : ""}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    name="externalCategory"
                    label="Intelliflo Document Category"
                    value={values.externalCategory}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.externalCategory && Boolean(errors.externalCategory)}
                    helperText={touched.externalCategory && errors.externalCategory}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    name="externalSubCategory"
                    label="Intelliflo Document Sub-Category"
                    value={values.externalSubCategory}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.externalSubCategory && Boolean(errors.externalSubCategory)}
                    helperText={touched.externalSubCategory && errors.externalSubCategory}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    name="internalCategory"
                    label="Hubwise Document Category"
                    value={values.internalCategory}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.internalCategory && Boolean(errors.internalCategory)}
                    helperText={touched.internalCategory && errors.internalCategory}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    name="internalFileCategory"
                    label="Hubwise Document File Category"
                    value={values.internalFileCategory}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.internalFileCategory && Boolean(errors.internalFileCategory)}
                    helperText={touched.internalFileCategory && errors.internalFileCategory}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} variant="outlined">
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    Create
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default DocumentCategoryTable;
